import {
  alpha,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React from 'react';
import { useSelector } from 'react-redux';
import { GameStatus } from '../../../constants/gameConstants';
import { findMeMemberKey } from '../../../helpers/findMe';
import { findRoundScheduleEntry, formatScheduleDate } from '../../../helpers/roundSchedule';

const StyledSectionTotalTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.common.white,
    borderBottom: 'none'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    borderBottom: 'none'
  }
}));

const SectionTotalsCard = ({
  section,
  isTv = false,
  event: eventProp,
  focusSectionId = null,
  focusTeamId = null,
  focusPlayerKey = null,
  focusToken = 0,
  showReserves = false
}) => {
  const storeEvent = useSelector((state) => state.event.event);
  // Public views read the event from Redux; the portal passes the event it
  // already loaded, because a portal official has no app session.
  const event = eventProp || storeEvent;
  const [expandedTeam, setExpandedTeam] = React.useState(null);
  const focusRowRef = React.useRef(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const sectionKey = section?.sectionId || section?.id || null;
  // A team can appear in more than one section, so a location only belongs to
  // the section recorded in the Find me result.
  const focusedTeamId =
    !focusSectionId || focusSectionId === sectionKey ? focusTeamId : null;

  // isTv renders as a semi-transparent overlay, so its colors need to adapt to the
  // active theme mode rather than being hardcoded to a single (dark) look.
  const isTvContainerBg = isDarkMode ? 'rgba(10, 18, 32, 0.9)' : 'rgba(255, 255, 255, 0.85)';
  const isTvTableBg = isDarkMode ? 'rgba(10, 18, 32, 0.7)' : 'rgba(255, 255, 255, 0.7)';
  const mutedTextColor = isDarkMode ? 'grey.400' : 'grey.800';

  // Show the "D" (Total Games Drawn) column when draws can actually happen: either playoffs
  // are disabled on the event's game format, or the format awards points for a draw.
  const showDrawColumn =
    !event.gameFormat?.playoffs ||
    (event.gameFormat?.drawPoints !== undefined &&
      event.gameFormat?.drawPoints !== null &&
      Number(event.gameFormat.drawPoints) !== 0);

  const handleToggleTeam = (teamId) => {
    setExpandedTeam(expandedTeam === teamId ? null : teamId);
  };

  // Selecting the same player again increments focusToken, so scroll again
  // even though the target team has not changed.
  React.useEffect(() => {
    if (!focusedTeamId || !focusToken) {
      return undefined;
    }

    setExpandedTeam(focusedTeamId);

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const frame = window.requestAnimationFrame(() => {
      focusRowRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [focusedTeamId, focusToken]);

  // The team's currentRound is the round of the first game it has not
  // completed, so it is the "next game" round - if the event has a date and
  // time scheduled for it.
  const getNextGameSchedule = (team) => {
    if (Number(team?.currentStatus) === GameStatus.COMPLETE) {
      return null;
    }

    return findRoundScheduleEntry(event?.roundSchedule, team?.currentRound);
  };

  const getStatusNameAndColor = (status) => {
    switch (status) {
      case 2:
        return { name: 'In Progress', color: 'blue.main' };
      case 3:
        return { name: 'Completed', color: 'green.main' };
      case 1:
        return { name: 'Not Started', color: mutedTextColor };
      case 4:
        return { name: 'Playoff', color: 'orange.main' };
      default:
        return { name: 'Unknown', color: mutedTextColor };
    }
  };

  if (section.sectionTotals && section.sectionTotals.teams.length !== 0) {
    let teamSetTotals = section.sectionTotals.teams;
    const mutableTeamSetTotals = Array.from(teamSetTotals);

    teamSetTotals = mutableTeamSetTotals;

    // Only add the column when at least one team has a scheduled next game.
    const showNextGameColumn = teamSetTotals.some((team) =>
      Boolean(getNextGameSchedule(team))
    );

    return (
      <TableContainer
        key={section.id}
        component={Paper}
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'blue.main',
          ...(isTv
            ? {
                backgroundColor: isTvContainerBg,
                backgroundImage: 'none',
                boxShadow: 'none',
                backdropFilter: 'none'
              }
            : {})
        }}>
        <Table
          padding="none"
          sx={isTv ? { backgroundColor: isTvTableBg } : undefined}>
          <TableHead>
            <TableRow>
              <StyledSectionTotalTableCell padding="normal">#</StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal">
                <Typography variant="body1" fontWeight={'bold'}>
                  {section.sectionName}
                </Typography>
                {section.venue && (
                  <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                    {section.venue}
                  </Typography>
                )}
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Games Played">P</Tooltip>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Games Won">W</Tooltip>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Games Lost">L</Tooltip>
              </StyledSectionTotalTableCell>
              {showDrawColumn && (
                <StyledSectionTotalTableCell padding="normal" align={'center'}>
                  <Tooltip title="Total Games Drawn">D</Tooltip>
                </StyledSectionTotalTableCell>
              )}
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Points">
                  <Typography variant="p" fontWeight={'bold'} color="white">
                    Pts
                  </Typography>
                </Tooltip>
              </StyledSectionTotalTableCell>
              {!event.gameFormat.setAsSkin && event.gameFormat.sets > 1 && (
                <StyledSectionTotalTableCell padding="normal" align={'center'}>
                  <Tooltip title="Total Sets Won">SW</Tooltip>
                </StyledSectionTotalTableCell>
              )}
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Net Points">Net</Tooltip>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                Rink
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                Round
              </StyledSectionTotalTableCell>
              {showNextGameColumn && (
                <StyledSectionTotalTableCell padding="normal" align={'center'}>
                  <Tooltip title="Date and time of the next game">Next Game</Tooltip>
                </StyledSectionTotalTableCell>
              )}
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                Status
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamSetTotals.map((team, index) => {
              if (team.dnq) {
                return null;
              }

              const isFocusedTeam =
                Boolean(focusedTeamId) && focusedTeamId === team.teamId;
              const teamPlayers = team.teamPlayers || [];
              const teamReserves = team.teamReserves || [];

              return (
                <React.Fragment key={team.teamId}>
                  <TableRow
                    ref={isFocusedTeam ? focusRowRef : undefined}
                    sx={
                      isFocusedTeam
                        ? {
                            backgroundColor: (activeTheme) =>
                              alpha(activeTheme.palette.warning.main, 0.18)
                          }
                        : undefined
                    }>
                    <StyledSectionTotalTableCell size="small" padding="normal">
                      {team.dnq ? (
                        <Typography variant="p" color={mutedTextColor} fontWeight={'bold'}>
                          DNQ
                        </Typography>
                      ) : (
                        <Typography variant="p" color="blue.main" fontWeight={'bold'}>
                          {index + 1}
                        </Typography>
                      )}
                    </StyledSectionTotalTableCell>
                    <StyledSectionTotalTableCell size="small" padding="none">
                      <Box display="flex" alignItems="center" my={1}>
                        {isTv !== true && (
                          <IconButton
                            size="small"
                            onClick={() => handleToggleTeam(team.teamId)}
                            sx={{ mr: 1 }}>
                            {expandedTeam === team.teamId ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                        <Typography
                          variant="p"
                          color={team.dnq ? mutedTextColor : 'inherit'}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleToggleTeam(team.teamId)}>
                          {team.teamName}
                        </Typography>
                        {isFocusedTeam && (
                          <Chip
                            size="small"
                            label="You"
                            color="warning"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </StyledSectionTotalTableCell>
                    <StyledSectionTotalTableCell size="small" align={'center'}>
                      <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                        {team.totalGames}
                      </Typography>
                    </StyledSectionTotalTableCell>
                    <StyledSectionTotalTableCell size="small" align={'center'}>
                      <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                        {team.totalGamesWon}
                      </Typography>
                    </StyledSectionTotalTableCell>
                    <StyledSectionTotalTableCell size="small" align={'center'}>
                      <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                        {team.totalGamesLost}
                      </Typography>
                    </StyledSectionTotalTableCell>
                    {showDrawColumn && (
                      <StyledSectionTotalTableCell size="small" align={'center'}>
                        <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                          {team.totalGamesDrawn}
                        </Typography>
                      </StyledSectionTotalTableCell>
                    )}
                    <StyledSectionTotalTableCell
                      size="small"
                      align={'center'}
                      sx={{ backgroundColor: 'blue.main' }}>
                      <Typography variant="p" fontWeight={'bold'} color="white">
                        {team.totalPoints}
                      </Typography>
                    </StyledSectionTotalTableCell>
                    {!event.gameFormat.setAsSkin && event.gameFormat.sets > 1 && (
                      <StyledSectionTotalTableCell size="small" align={'center'}>
                        <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                          {team.setsWon}
                        </Typography>
                      </StyledSectionTotalTableCell>
                    )}
                    <StyledSectionTotalTableCell size="small" align={'center'}>
                      <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                        {team.totalNet}
                      </Typography>
                    </StyledSectionTotalTableCell>
                    <StyledSectionTotalTableCell size="small" align={'center'}>
                      <Tooltip title="Green/Rink">
                        <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                          {team.rink ? team.rink : 'N/A'}
                        </Typography>
                      </Tooltip>
                    </StyledSectionTotalTableCell>
                    <StyledSectionTotalTableCell size="small" align={'center'} padding="normal">
                      <Tooltip title="Current Round">
                        <Typography variant="p" color={team.dnq ? mutedTextColor : 'inherit'}>
                          {team.currentRound ? team.currentRound : 'N/A'}
                        </Typography>
                      </Tooltip>
                    </StyledSectionTotalTableCell>
                    {showNextGameColumn && (
                      <StyledSectionTotalTableCell size="small" align={'center'} padding="normal">
                        {(() => {
                          const entry = getNextGameSchedule(team);

                          if (!entry) {
                            return (
                              <Typography
                                variant="p"
                                color={team.dnq ? mutedTextColor : 'inherit'}>
                                N/A
                              </Typography>
                            );
                          }

                          return (
                            <>
                              <Typography
                                variant="p"
                                color={team.dnq ? mutedTextColor : 'inherit'}>
                                {formatScheduleDate(entry.date)}
                              </Typography>
                              <Typography
                                variant="p"
                                color={team.dnq ? mutedTextColor : 'inherit'}>
                                {entry.startTime}
                              </Typography>
                            </>
                          );
                        })()}
                      </StyledSectionTotalTableCell>
                    )}
                    {team.currentStatus && (
                      <StyledSectionTotalTableCell size="small" align={'center'} padding="normal">
                        <Tooltip title={getStatusNameAndColor(team.currentStatus).name}>
                          <Typography
                            variant="p"
                            color={getStatusNameAndColor(team.currentStatus).color}
                            fontWeight={'bold'}>
                            {getStatusNameAndColor(team.currentStatus).name}
                          </Typography>
                        </Tooltip>
                      </StyledSectionTotalTableCell>
                    )}
                  </TableRow>

                  {/* Collapsible row for team details */}
                  <TableRow>
                    <StyledSectionTotalTableCell
                      colSpan={
                        9 +
                        (showDrawColumn ? 1 : 0) +
                        (!event.gameFormat.setAsSkin && event.gameFormat.sets > 1 ? 1 : 0) +
                        (showNextGameColumn ? 1 : 0) +
                        (team.currentStatus ? 1 : 0)
                      }
                      sx={{ padding: 0, border: 'none' }}>
                      <Collapse in={expandedTeam === team.teamId} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {teamPlayers.length > 0
                              ? teamPlayers.map((member, playerIndex) => {
                                  const isFocusedPlayer =
                                    isFocusedTeam &&
                                    Boolean(focusPlayerKey) &&
                                    findMeMemberKey(member) === focusPlayerKey;

                                  return (
                                    <React.Fragment
                                      key={`${findMeMemberKey(member)}-${playerIndex}`}>
                                      {playerIndex > 0 && ', '}
                                      <Box
                                        component="span"
                                        sx={
                                          isFocusedPlayer
                                            ? {
                                                fontWeight: 'bold',
                                                textDecoration: 'underline'
                                              }
                                            : undefined
                                        }>
                                        {`${member?.name ?? ''} ${member?.surname ?? ''}`.trim()}
                                      </Box>
                                    </React.Fragment>
                                  );
                                })
                              : 'No team members found'}
                          </Typography>
                          {showReserves && teamReserves.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 0.5 }}>
                              Reserves:{' '}
                              {teamReserves
                                .map((reserve) =>
                                  `${reserve?.name ?? ''} ${reserve?.surname ?? ''}`.trim()
                                )
                                .filter(Boolean)
                                .join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </StyledSectionTotalTableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow key={'footer'} sx={{ backgroundColor: 'info.main' }} padding="none">
              <TableCell colSpan={'100%'} padding={'none'} align={'center'} sx={{ p: 1 }}>
                {section.sectionWinningTeam ? (
                  <Typography variant="caption" color={'white'} fontWeight={'bold'}>
                    Section won by {section.sectionWinningTeam.teamName}.{' '}
                    {section.winnerPlaysSection !== '' &&
                      'Plays ' + section.winnerPlaysSection + ' winner.'}
                  </Typography>
                ) : section.winnerPlaysSection !== '' ? (
                  <Typography variant="caption" color={'white'} fontWeight={'bold'}>
                    Section winner plays {section.winnerPlaysSection} winner.
                  </Typography>
                ) : null}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  } else {
    return (
      <TableContainer
        key={section.id}
        component={Paper}
        sx={{
          borderRadius: 1,
          maxWidth: 400,
          border: '1px solid',
          borderColor: 'blue.main',
          ...(isTv
            ? {
                backgroundColor: isTvContainerBg,
                backgroundImage: 'none',
                boxShadow: 'none',
                backdropFilter: 'none'
              }
            : {})
        }}>
        <Table
          padding="none"
          sx={isTv ? { backgroundColor: isTvTableBg } : undefined}>
          <TableHead>
            <TableRow>
              <StyledSectionTotalTableCell padding="normal">
                <Typography variant="body1" fontWeight={'bold'}>
                  {section.sectionName}
                </Typography>
                {section.venue && (
                  <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                    {section.venue}
                  </Typography>
                )}
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <StyledSectionTotalTableCell size="small" padding="normal">
                No data available yet, check back again later.
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
};

export default SectionTotalsCard;
